import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

// Schema v1 persisted store for the whole app
// {
//   version: 1,
//   plan: '2004',
//   cbcEnabled: boolean,
//   subjects: Array<Subject>,
//   orientation: { id: string|null },
//   schedule: Array<any>
// }

/**
 * @typedef {'pendiente'|'cursando'|'aprobada'} SubjectStatus
 * @typedef {{ id:string; nombre:string; area:'CPC'|'CPO'; status:SubjectStatus; nota?:number|null; mes?:number|null; anio?:number|null; updatedAt:string }} Subject
 */

const STORAGE_KEY = 'app@v1';

const StoreContext = createContext(null);

function nowIso() { return new Date().toISOString(); }

function migrateLegacy() {
  try {
    /** @type {any} */
    const legacyMateriasRaw = localStorage.getItem('materias');
    const legacyCBC = localStorage.getItem('cbcAprobado');
    const legacyOrient = localStorage.getItem('orientacionSeleccionada');
    const agendaRaw = localStorage.getItem('agenda@v1');
    const subjects = [];
    if (legacyMateriasRaw) {
      const materias = JSON.parse(legacyMateriasRaw);
      Object.values(materias || {}).forEach((m) => {
        if (!m || !m.id) return;
        const status = m.estado === 'CURSADA_APROBADA' ? 'aprobada' : (m.estado === 'CURSANDO' ? 'cursando' : 'pendiente');
        const nota = typeof m.notaFinal === 'number' && m.notaFinal >= 0 ? m.notaFinal : null;
        let mes = null, anio = null;
        if (m.fecha) {
          const parts = String(m.fecha).split('-');
          if (parts.length >= 2) { mes = Number(parts[1]); anio = Number(parts[0]); }
        }
        subjects.push({ id: m.id, nombre: m.nombre || m.id, area: (m.categoria || '').includes('CPO') ? 'CPO' : 'CPC', status, nota, mes, anio, updatedAt: nowIso() });
      });
    }
    const schedule = agendaRaw ? (JSON.parse(agendaRaw)?.eventos || []) : [];
    return {
      version: 1,
      plan: '2004',
      cbcEnabled: legacyCBC ? JSON.parse(legacyCBC) : false,
      subjects,
      orientation: legacyOrient ? JSON.parse(legacyOrient) : { id: null },
      schedule,
    };
  } catch {
    return null;
  }
}

function getInitialState() {
  const empty = { version: 1, plan: '2004', cbcEnabled: false, subjects: [], orientation: { id: null }, schedule: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return migrateLegacy() || empty;
    const parsed = JSON.parse(raw);
    if (!parsed.version) parsed.version = 1;
    return parsed;
  } catch {
    return migrateLegacy() || empty;
  }
}

const A = {
  SET_SUBJECT_STATUS: 'SET_SUBJECT_STATUS',
  SET_SUBJECT_GRADE: 'SET_SUBJECT_GRADE',
  BULK_IMPORT: 'BULK_IMPORT',
  RESET_PROGRESS: 'RESET_PROGRESS',
  TOGGLE_CBC: 'TOGGLE_CBC',
  SET_ORIENTATION: 'SET_ORIENTATION',
  SET_SCHEDULE: 'SET_SCHEDULE',
};

function reducer(state, action) {
  switch (action.type) {
    case A.SET_SUBJECT_STATUS: {
      const { id, status } = action.payload;
      return { ...state, subjects: state.subjects.map((s) => s.id === id ? { ...s, status, updatedAt: nowIso() } : s) };
    }
    case A.SET_SUBJECT_GRADE: {
      const { id, nota, mes, anio } = action.payload;
      return { ...state, subjects: state.subjects.map((s) => s.id === id ? { ...s, nota, mes, anio, updatedAt: nowIso() } : s) };
    }
    case A.BULK_IMPORT: {
      const next = { ...state, ...action.payload, version: 1 };
      return next;
    }
    case A.RESET_PROGRESS: {
      return { ...state, cbcEnabled: false, subjects: state.subjects.map((s) => ({ ...s, status: 'pendiente', nota: null, mes: null, anio: null, updatedAt: nowIso() })) };
    }
    case A.TOGGLE_CBC: {
      return { ...state, cbcEnabled: typeof action.payload === 'boolean' ? action.payload : !state.cbcEnabled };
    }
    case A.SET_ORIENTATION: {
      return { ...state, orientation: { id: action.payload || null } };
    }
    case A.SET_SCHEDULE: {
      return { ...state, schedule: Array.isArray(action.payload) ? action.payload : state.schedule };
    }
    default:
      return state;
  }
}

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  const api = useMemo(() => ({
    state,
    setSubjectStatus: (id, status) => dispatch({ type: A.SET_SUBJECT_STATUS, payload: { id, status } }),
    setSubjectGrade: (id, payload) => dispatch({ type: A.SET_SUBJECT_GRADE, payload: { id, ...payload } }),
    bulkImport: (payload) => dispatch({ type: A.BULK_IMPORT, payload }),
    resetProgress: () => dispatch({ type: A.RESET_PROGRESS }),
    toggleCBC: (enabled) => dispatch({ type: A.TOGGLE_CBC, payload: enabled }),
    setOrientation: (id) => dispatch({ type: A.SET_ORIENTATION, payload: id }),
    setSchedule: (arr) => dispatch({ type: A.SET_SCHEDULE, payload: arr }),
    // selectors
    selectSubjects: () => state.subjects,
    selectByStatus: (status) => state.subjects.filter((s) => s.status === status),
    selectProgress: () => {
      const total = state.subjects.length || 1;
      const aprobadas = state.subjects.filter((s) => s.status === 'aprobada').length;
      const cursando = state.subjects.filter((s) => s.status === 'cursando').length;
      const pendiente = total - aprobadas - cursando;
      const porcentaje = Math.round((aprobadas / total) * 100);
      const cpc = state.subjects.filter((s) => s.area === 'CPC' && s.status === 'aprobada').length;
      const cpo = state.subjects.filter((s) => s.area === 'CPO' && s.status === 'aprobada').length;
      return { total, aprobadas, cursando, pendiente, porcentaje, cpc, cpo, cbc: state.cbcEnabled };
    },
  }), [state]);

  return <StoreContext.Provider value={api}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}

// Optional variant for places that can render with or without provider
export function useOptionalStore() {
  return useContext(StoreContext);
}


